import React, { ReactNode } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import Status500 from './500';

interface ErrorBoundaryProps extends RouteComponentProps {
  children: ReactNode;
  fallbackView?: ReactNode;
  forceShow?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = { hasError: props.forceShow || false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { location: prevLocation } = prevProps;
    const { location } = this.props;

    if (prevLocation.pathname !== location.pathname) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) { // eslint-disable-line class-methods-use-this
    const { componentStack } = info;

    window?.Sentry?.captureException?.(error, {
      contexts: { react: { componentStack } },
      level: 'fatal',
      tags: { errorBoundary: 'app' },
    });
  }

  render() {
    const { children, fallbackView = <Status500 testid='boundary'/> } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return fallbackView;
    }
    return children;
  }
}

export default withRouter(ErrorBoundary);
